import { useMemo, useState, type ReactElement } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table'

import { Link } from '@atoms'
import Badge from '@atoms/Badge'
import { ProgressBar } from '@components/molecules/ProgressBar'
import { DataTableRoot, DataTableContent, DataTableFooter } from '@components/molecules/DataTable'
import EmptyState from '@components/molecules/EmptyState'
import { ExpandableText } from '@components/molecules/ExpandableText'
import LoadingWrapper from '@components/molecules/LoadingWrapper'
import { PageLayout } from '@components/layouts/PageLayout'
import {
  GroupPageHeader,
  type GroupPageHeaderProps,
} from '@frontend/contexts/supporters-management/features/group-page/GroupPageHeader'
import { SelectField } from '@components/molecules/SelectField'
import { ExportRosterButton } from '@frontend/contexts/events_management/features/export_roster/ExportRosterButton'
import { scopedTranslation } from '@utils/I18n'

import { router } from '@inertiajs/react'
import useEventsFilteringStore, {
  EventsFilteringProvider,
} from '../features/filter_events/stores/useEventsFilteringStore'
import EventSearchEmptyState from '../features/filter_events/EventSearchEmptyState'
import { EventSearchField } from '../features/filter_events/EventSearchField'
import Pagination from '@components/molecules/Pagination'

const t = scopedTranslation('shifts_overview')
const tSort = scopedTranslation('shared.sort.events')
const tShared = scopedTranslation('shared')

type ShiftSlot = {
  totalCount?: number
  assignedCount?: number
}

type ShiftCoverage = {
  totalShifts: number
  assignedShifts: number
}

type Shift = {
  shiftSlots?: ShiftSlot
}

type Roster = {
  shifts?: {
    nodes?: Shift[]
    totalCount?: number
  }
}

type EventRoutes = {
  editEvent: string
  editShifts: string
}

export type Event = {
  id: string
  name: string
  startTime: string
  endTime?: string
  fullVenueAddress?: string
  roster: Roster
  routes: EventRoutes
  group: { id: string }
}

type Routes = {
  newEventRoute: string
}

type ShiftsOverviewProps = {
  groupHeader?: GroupPageHeaderProps | null
  events: {
    nodes: Event[]
    totalCount: number
    totalPages: number
    pageSize: number
  }
  routes: Routes
  activeSearch: {
    sortBy?: string
    eventName?: string
    page?: number
  }
}

function calculateShiftCoverage(shifts: Shift[]): ShiftCoverage {
  return shifts.reduce(
    (acc, shift) => {
      const shiftTotal = shift.shiftSlots?.totalCount ?? 0
      const shiftAssigned = shift.shiftSlots?.assignedCount ?? 0
      acc.totalShifts += shiftTotal
      acc.assignedShifts += shiftAssigned
      return acc
    },
    { totalShifts: 0, assignedShifts: 0 }
  )
}

function getFormattedEndDate(event: Event): string | null {
  if (!event.endTime) return null

  const now = dayjs()
  const start = dayjs(event.startTime)
  const end = dayjs(event.endTime)

  if (end.isAfter(now) && end.diff(start, 'hour') >= 24) {
    return end.format('dddd, D MMMM')
  }

  return null
}

function useEventColumns() {
  return useMemo<Array<ColumnDef<Event>>>(
    () => [
      {
        accessorKey: 'name',
        header: () => t('table_header.event'),
        meta: { allowWrap: true },
        cell: ({ row }) => {
          const { name, fullVenueAddress = '', routes } = row.original
          return (
            <div className="tw:flex tw:max-w-80 tw:flex-col">
              <a
                href={routes.editEvent}
                className="tw:text-primary-700 tw:underline tw:font-medium tw:text-base tw:w-fit"
                aria-label={t('aria_labels.edit_event', { name })}
              >
                {name}
              </a>
              <ExpandableText
                text={fullVenueAddress || t('event_row.virtual_event')}
                expandLabel={t('event_row.show_full_address')}
              />
            </div>
          )
        },
      },
      {
        accessorKey: 'startTime',
        header: () => t('table_header.date'),
        cell: ({ row }) => {
          const formattedStartDate = dayjs(row.original.startTime).format('dddd, D MMMM')
          const formattedEndDate = getFormattedEndDate(row.original)
          return (
            <div>
              {formattedStartDate}
              {formattedEndDate && (
                <>
                  <span> - </span>
                  <br />
                  {formattedEndDate}
                </>
              )}
            </div>
          )
        },
      },
      {
        id: 'shiftCoverage',
        header: () => t('table_header.shift_coverage'),
        cell: ({ row }) => {
          const shifts = row.original.roster.shifts?.nodes ?? []
          const { totalShifts, assignedShifts } = calculateShiftCoverage(shifts)
          const shiftCoverage = `${assignedShifts}/${totalShifts}`
          const shiftCoveragePercentage = totalShifts > 0 ? (assignedShifts / totalShifts) * 100 : 0
          return (
            <div className="tw:flex tw:gap-3 tw:justify-center tw:items-center">
              <ProgressBar progress={shiftCoveragePercentage} useTrafficLightColors={true} />
              {shiftCoverage}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: () => null,
        meta: { allowWrap: true },
        cell: ({ row }) => {
          const { routes } = row.original
          return (
            <div className="tw:flex tw:flex-wrap tw:justify-end tw:items-center tw:gap-y-2">
              <Link href={routes.editShifts} rank="link" leadingIcon="pencil-02" as="inertiaLink">
                {t('event_row.edit_shifts')}
              </Link>
              <div className="tw:pl-2">
                <ExportRosterButton event={row.original} />
              </div>
            </div>
          )
        },
      },
    ],
    []
  )
}

function ShiftsOverviewEmptyState(): ReactElement {
  const tEmptyState = scopedTranslation('shifts_overview.empty_states.no_events_with_shifts')

  return <EmptyState headline={tEmptyState('headline')} subtext={tEmptyState('subtext')} iconType={'calendar-date'} />
}

function SortEvents(): ReactElement {
  const currentSort = useEventsFilteringStore((state) => state.currentSort)
  const setCurrentSort = useEventsFilteringStore((state) => state.setCurrentSort)

  const sortOptions = [
    { value: 'start_time asc', label: tSort('start_time_asc') },
    { value: 'start_time desc', label: tSort('start_time_desc') },
    { value: 'name asc', label: tSort('event_name_asc') },
    { value: 'name desc', label: tSort('event_name_desc') },
    { value: 'shift_coverage asc', label: tSort('shift_coverage_lowest') },
    { value: 'shift_coverage desc', label: tSort('shift_coverage_highest') },
  ]

  return (
    <SelectField
      name="sort_by"
      label={tShared('labels.sort_by')}
      options={sortOptions}
      value={currentSort}
      onValueChange={setCurrentSort}
    />
  )
}

function redirectToFirstPageIfInvalid(currentPage: number, totalPages: number) {
  if (currentPage > totalPages) {
    const url = new URL(window.location.href)
    url.searchParams.set('page', '1')
    router.visit(url.pathname + url.search)
  }
}

export default function ShiftsOverview({ groupHeader, events, routes, activeSearch }: ShiftsOverviewProps) {
  const { nodes: eventNodes = [], totalCount, totalPages, pageSize } = events ?? {}

  const pageParamPresent = activeSearch.page != null
  if (pageParamPresent) {
    const currentPage = activeSearch.page ?? 1
    redirectToFirstPageIfInvalid(currentPage, totalPages)
  }

  const partialReloadKeys = ['events', 'activeSearch']
  const matchingEventsCount = totalCount

  const eventCount = eventNodes.length
  const [searchLoading, setSearchLoading] = useState(false)

  const columns = useEventColumns()

  const table = useReactTable({
    data: eventNodes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
  })

  const showInitialEmptyState = eventCount === 0 && !activeSearch.eventName
  const showSearchEmptyState = !showInitialEmptyState && eventCount === 0

  const header = groupHeader ? (
    <GroupPageHeader {...groupHeader} />
  ) : (
    <PageLayout.Header
      icon="calendar"
      heading={t('page_header')}
      actions={
        <Link href={routes.newEventRoute} rank="primary" trailingIcon="plus-circle">
          {t('shifts_overview.add_new_event')}
        </Link>
      }
    />
  )

  if (showInitialEmptyState) {
    return (
      <PageLayout.Root>
        {header}
        <PageLayout.Body>
          <ShiftsOverviewEmptyState />
        </PageLayout.Body>
      </PageLayout.Root>
    )
  }

  return (
    <PageLayout.Root>
      {header}
      <PageLayout.Body>
        <EventsFilteringProvider
          initProps={{
            searchTerm: activeSearch.eventName,
            currentSort: activeSearch.sortBy ?? undefined,
            setLoading: setSearchLoading,
          }}
        >
          <div
            id="table-wrapper"
            className={clsx('tw:rounded-lg tw:border tw:border-gray-200', 'tw:bg-white tw:w-full')}
          >
            <div className={clsx('tw:flex tw:justify-between tw:gap-5 tw:items-center tw:px-6 tw:pb-4 tw:pt-6')}>
              <div className={clsx('tw:flex tw:items-center tw:gap-5')}>
                <h2 className={clsx('tw:text-gray-900 tw:text-lg tw:font-medium')}>
                  {t('shifts_overview.upcoming_events')}
                </h2>
                <Badge color={'primary'}>{t('shifts_overview.event_count', { count: totalCount })}</Badge>
              </div>
            </div>
            <div className={clsx('tw:max-w-[600px] tw:px-6 tw:pb-6 tw:text-sm tw:text-gray-700')}>
              {t('page_description')}
            </div>

            <div className="tw:flex tw:items-end tw:gap-4 tw:flex-wrap tw:md:flex-nowrap tw:px-6 tw:pb-6">
              <div className="tw:w-full tw:md:w-[300px]">
                <EventSearchField />
              </div>

              <div className="tw:w-fit">
                <SortEvents />
              </div>
            </div>

            <LoadingWrapper loading={searchLoading}>
              <DataTableRoot table={table}>
                <DataTableContent
                  className="tw:rounded-none tw:border-x-0"
                  emptyState={showSearchEmptyState ? <EventSearchEmptyState /> : undefined}
                />
                <DataTableFooter
                  totalCount={totalCount}
                  pageSize={pageSize}
                  currentPage={activeSearch.page ?? 1}
                  label={t('footer.label')}
                />
              </DataTableRoot>

              {matchingEventsCount > 0 && totalPages > 1 && (
                <div className="tw:py-6">
                  <Pagination totalPages={totalPages ?? 1} only={partialReloadKeys} />
                </div>
              )}
            </LoadingWrapper>
          </div>
        </EventsFilteringProvider>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
